import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { TruthManagement } from '../../services/truth-management';
import { ExtractionRunService, ExtractionRun, PDFData } from '../../services/extraction-run.service';
import { NotificationService } from '../../services/notification.service';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { Subject, of } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { PageHeaderComponent } from '../page-header/page-header.component';

interface ExtractedListField {
  fieldName: string;
  values: any[];  // Support multiple values per attribute
  isExtracted: boolean;
  type: 'list';
  feature?: string;  // Feature type from schema (e.g., 'signature', 'date', 'checkbox', 'seal')
  fieldDatatype?: string;  // Field datatype from schema (e.g., 'array', 'string', 'number')
  isAdditional?: boolean;  // True when the field has no field schema entry (e.g. name/address split output)
}

interface TableRow {
  [columnName: string]: string;  // Column name -> extracted value
}

interface ExtractedTableField {
  fieldName: string;
  columns: string[];  // List of column names
  rows: TableRow[];   // Array of rows with column values
  isExtracted: boolean;
  type: 'table';
  isAdditional?: boolean;  // True when the field has no field schema entry (e.g. name/address split output)
}

type ExtractedField = ExtractedListField | ExtractedTableField;

@Component({
  selector: 'app-data-extraction-result',
  imports: [CommonModule, FormsModule, PdfViewerModule, PageHeaderComponent],
  templateUrl: './data-extraction-result.html',
  styleUrls: ['./data-extraction-result.scss'],
})
export class DataExtractionResultComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedRun: ExtractionRun | null = null;
  
  // PDF viewer properties
  currentPage: number = 1;
  totalPages: number = 0;
  pdfReady: boolean = false;  // Flag to track if PDF is fully loaded and ready
  zoom: number = 1;
  pageInput: string = '1';
  zoomPercentage: number = 100;
  pdfSrc: any = null;
  isLoadingPdf: boolean = false;
  pageRotations: Map<number, number> = new Map(); // Store rotation per page (page number -> degrees)
  
  // Ground truth properties
  fieldSchemaFields: any[] = [];
  extractedFields: ExtractedField[] = [];
  extractedFieldsCount: number = 0; // Count of schema fields with extracted data (additional fields excluded)
  fieldsLoading: boolean = false;
  extractionStatus: string = 'Pending'; // Track current extraction status
  
  // UI state
  isAuthenticated: boolean = false;
  splitPosition: number = 65; // Default 65/35 split
  isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartSplit: number = 0;
  searchTerm: string = ''; // Search field for filtering extracted fields
  navigationSource: string = ''; // Track source page for dynamic back button tooltip
  
  private destroy$ = new Subject<void>();
  @ViewChild('pdfContainer') pdfContainer: ElementRef | undefined;  private boundWheelHandler = this.onPdfWheel.bind(this);
  constructor(
    public router: Router,
    public authService: AuthService,
    private truthManagement: TruthManagement,
    private extractionRunService: ExtractionRunService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private clientCapability: ClientCapabilityService,
  ) {
    this.isAuthenticated = this.authService.isAuthenticated;
  }

  ngOnInit(): void {
    // Get navigation source from service
    this.navigationSource = this.extractionRunService.getNavigationSource();
    
    // Get selected run from service
    this.selectedRun = this.extractionRunService.getSelectedRun();
    
    if (!this.selectedRun) {
      // No run selected, redirect back to data extraction page
      this.router.navigate(['/data_extraction']);
      return;
    }

    // Load PDF and field schema + extracted data in parallel
    this.loadPdfInMemory();
    this.loadFieldSchemaAndExtractedData();
  }

  ngAfterViewInit(): void {
    // Attach wheel event handler for scroll-based zoom
    if (this.pdfContainer?.nativeElement) {
      this.pdfContainer.nativeElement.addEventListener('wheel', this.boundWheelHandler, { passive: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up wheel event handler
    if (this.pdfContainer?.nativeElement) {
      this.pdfContainer.nativeElement.removeEventListener('wheel', this.boundWheelHandler);
    }
    
    // NOTE: Do NOT revoke blob URL or clear pdfSrc here!
    // The blob URL is cached in ExtractionRunService.pdfCache and 
    // persists across navigation. The service manages cleanup.
  }

  /**
   * Load PDF from service cache (or fetch if not cached)
   * The service caches the blob URL, so component doesn't manage lifecycle
   */
  private loadPdfInMemory(): void {
    if (!this.selectedRun) {
      return;
    }

    this.isLoadingPdf = true;
    // Reset page to 1 when loading new PDF
    this.currentPage = 1;
    this.pageInput = '1';
    this.totalPages = 0;

    this.extractionRunService
      .loadPdfForRun(this.selectedRun.id, this.selectedRun.documentModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pdfData: PDFData) => {
          // Use the PDFData returned by the service observable
          // Do NOT access via selectedRun.pdfData as it may not be synchronized on first load
          this.pdfSrc = pdfData.blobUrl || null;
          // Reset totalPages - will be updated when PDF loads via onPdfDocumentLoad
          this.totalPages = 0;
          this.isLoadingPdf = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoadingPdf = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Load field schema and extracted data from APIs
   * Checks current status first - only polls if not yet COMPLETED
   * Shows toaster only when polling transitions to COMPLETED
   */
  private loadFieldSchemaAndExtractedData(): void {
    if (!this.selectedRun) {
      return;
    }

    this.fieldsLoading = true;

    // First, fetch the field schema for the document model
    this.truthManagement.getFieldSchemas(this.selectedRun.documentModel)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((fieldSchemaResponse: any) => {
          // Store field schema for later use
          this.fieldSchemaFields = fieldSchemaResponse?.fields || [];
          
          // Check current status WITHOUT polling - if already COMPLETED, skip polling
          return this.truthManagement.pollExtractionStatus(this.selectedRun!.id).pipe(
            switchMap((initialResponse: any) => {
              // Update status from response
              this.extractionStatus = initialResponse?.status || 'Pending';
              this.cdr.markForCheck();
              
              // If already COMPLETED, just return the response without polling/toaster
              if (initialResponse?.status === 'COMPLETED') {
                return of(initialResponse);
              }
              
              // Status is not COMPLETED yet - start polling until it completes
              // This is where the toaster WILL show (on actual completion)
              return this.extractionRunService.pollUntilComplete(
                this.selectedRun!.id,
                this.selectedRun!.documentModel,
                (runId: string) => this.truthManagement.pollExtractionStatus(runId),
                (docModel: string, runId: string) => {
                  this.notificationService.showExtractionCompleted(docModel, `Request ID: ${runId}`);
                }
              ).pipe(
                switchMap((completedResponse: any) => {
                  // Update status when polling completes
                  this.extractionStatus = completedResponse?.status || 'COMPLETED';
                  this.cdr.markForCheck();
                  return of(completedResponse);
                })
              );
            })
          );
        })
      )
      .subscribe({
        next: (pollResponse: any) => {
          this.fieldsLoading = false;
          const extractedDataMap = pollResponse?.attributes || {};
          
          // Combine field schema with extracted data
          this.displayExtractedDataWithSchema(extractedDataMap);
          
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.fieldsLoading = false;
          
          // Show error toast when polling or API fails
          const errorMessage = error?.error?.message || error?.message || 'Failed to fetch extraction results';
          this.notificationService.showError(errorMessage);
          
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Normalize field name for case-insensitive and underscore-insensitive matching
   * Converts to lowercase and replaces underscores with spaces
   */
  private normalizeFieldName(fieldName: string): string {
    return fieldName.toLowerCase().replace(/_/g, ' ').trim();
  }

  /**
   * Detect if extracted value is table-formatted data
   * Table format: array of objects where each item has column names as keys,
   * and each column is an object with 'Value' property
   * List format: array of objects with direct 'Value' property
   */
  private isTableFormatData(value: any): boolean {
    if (!Array.isArray(value) || value.length === 0) {
      return false;
    }

    const firstItem = value[0];
    if (typeof firstItem !== 'object' || firstItem === null) {
      return false;
    }

    // Check if first item has nested objects with 'Value' property (table format)
    // vs direct 'Value' property (list format)
    const keys = Object.keys(firstItem);
    
    // If there's a direct 'Value' key, it's a list format
    if (keys.includes('Value')) {
      return false;
    }

    // Check if keys have values that are objects with 'Value' property
    for (const key of keys) {
      const item = firstItem[key];
      if (typeof item === 'object' && item !== null && 'Value' in item) {
        return true;
      }
    }

    return false;
  }

  /**
   * Extract table columns and rows from table-formatted data
   * @param data Array of objects representing table rows
   * @returns Object with columns array and rows array
   */
  private extractTableData(data: any[]): { columns: string[]; rows: TableRow[] } {
    const columns: string[] = [];
    const rows: TableRow[] = [];

    if (!Array.isArray(data) || data.length === 0) {
      return { columns, rows };
    }

    // Get column names from first row
    const firstRow = data[0];
    if (typeof firstRow === 'object' && firstRow !== null) {
      columns.push(...Object.keys(firstRow).filter(key => typeof firstRow[key] === 'object'));
    }

    // Extract values from each row
    data.forEach((rowData: any) => {
      const row: TableRow = {};
      
      columns.forEach(columnName => {
        const columnData = rowData[columnName];
        if (typeof columnData === 'object' && columnData !== null && 'Value' in columnData) {
          row[columnName] = columnData.Value || '';
        } else {
          row[columnName] = '';
        }
      });

      rows.push(row);
    });

    return { columns, rows };
  }

  /**
   * Find extracted data value for a schema field using fuzzy matching
   * Matches case-insensitively and treats underscores as spaces
   * Records the matched raw key in consumedKeys so leftover (non-schema) attributes can be found afterwards
   */
  private findExtractedValue(
    schemaFieldName: string,
    extractedData: Record<string, any>,
    consumedKeys?: Set<string>
  ): any {
    const normalizedSchemaName = this.normalizeFieldName(schemaFieldName);

    // Search through all keys in extractedData for a case/underscore-insensitive match
    for (const [dataKey, dataValue] of Object.entries(extractedData)) {
      const normalizedDataKey = this.normalizeFieldName(dataKey);
      if (normalizedDataKey === normalizedSchemaName) {
        consumedKeys?.add(dataKey);
        return dataValue;
      }
    }

    return undefined;
  }

  /**
   * Build a displayable field entry from a raw extracted attribute value
   * Shared by schema-matched fields and leftover (non-schema) attributes such as
   * name-split/address-split generated fields, so both render identically
   */
  private buildFieldEntry(
    fieldName: string,
    extractedValue: any,
    feature: string = '',
    fieldDatatype: string = '',
    isAdditional: boolean = false
  ): ExtractedField {
    if (Array.isArray(extractedValue) && this.isTableFormatData(extractedValue)) {
      const { columns, rows } = this.extractTableData(extractedValue);
      if (rows.length > 0) {
        return { fieldName, columns, rows, isExtracted: true, type: 'table', isAdditional } as ExtractedTableField;
      }
      return { fieldName, columns: [], rows: [], isExtracted: false, type: 'table', isAdditional } as ExtractedTableField;
    }

    const extractedValues: any[] = [];

    if (Array.isArray(extractedValue)) {
      extractedValue.forEach((item: any) => {
        const value = item?.['Value'];
        if (value !== null && value !== undefined && value !== '') {
          extractedValues.push(value);
        }
      });
    } else if (extractedValue && !Array.isArray(extractedValue)) {
      const value = extractedValue?.['Value'];
      if (value !== null && value !== undefined && value !== '') {
        extractedValues.push(value);
      }
    }

    return {
      fieldName,
      values: extractedValues,
      isExtracted: extractedValues.length > 0,
      type: 'list',
      feature: feature.toLowerCase(),
      fieldDatatype: fieldDatatype.toLowerCase(),
      isAdditional
    } as ExtractedListField;
  }

  /**
   * Display all fields from schema with extracted data populated where available
   * Handles both list-formatted and table-formatted data
   * Fields with data are shown first, then empty fields
   * Matches field names case-insensitively and treats underscores as spaces
   */
  private displayExtractedDataWithSchema(extractedData: Record<string, any>): void {
    const fieldsWithData: ExtractedField[] = [];
    const fieldsWithoutData: ExtractedField[] = [];
    const consumedKeys = new Set<string>();

    // Process all fields from the schema
    this.fieldSchemaFields.forEach((schemaField: any) => {
      const fieldName = schemaField.field_name || schemaField.name;
      const feature = schemaField.feature || '';
      const fieldDatatype = schemaField.field_datatype || '';

      // Look for extracted data for this field (case-insensitive, underscore-insensitive)
      const extractedValue = this.findExtractedValue(fieldName, extractedData, consumedKeys);
      const entry = this.buildFieldEntry(fieldName, extractedValue, feature, fieldDatatype);

      (entry.isExtracted ? fieldsWithData : fieldsWithoutData).push(entry);
    });

    // Store count of schema fields that actually have extracted data (leftover/additional
    // fields below are excluded from both this count and the schema total)
    this.extractedFieldsCount = fieldsWithData.length;

    // Leftover attributes present in the API response but absent from the field schema
    // (e.g. name-split/address-split generated fields like First Name, City, ZIP Code)
    // Shown with an "Additional" badge; not counted towards the extracted/total field counts
    const additionalFields: ExtractedField[] = [];
    Object.entries(extractedData).forEach(([dataKey, dataValue]) => {
      if (consumedKeys.has(dataKey)) {
        return;
      }
      const entry = this.buildFieldEntry(dataKey, dataValue, '', '', true);
      if (entry.isExtracted) {
        additionalFields.push(entry);
      }
    });

    // Combine: schema fields with data, then additional fields, then schema fields without data
    this.extractedFields = [...fieldsWithData, ...additionalFields, ...fieldsWithoutData];
  }

  /**
   * Get dynamic tooltip text for back button based on navigation source
   */
  getBackButtonTooltip(): string {
    switch (this.navigationSource) {
      case 'extraction-reports':
        return 'Back to Extraction Report';
      case 'data-extraction':
        return 'Back to Data Extraction';
      default:
        return 'Go back';
    }
  }

  /**
   * Navigate back to the previous page in browser history
   * Uses location.back() which works with all navigation sources (extraction-reports, data-extraction-page, etc.)
   */
  goBack(): void {
    this.location.back();
  }

  /**
   * Navigate to home dashboard
   */
  navigateHome(): void {
    this.router.navigate(['/home']);
  }

  /**
   * Navigate to data extraction page
   */
  navigateToDataExtraction(): void {
    this.router.navigate(['/data_extraction']);
  }

  /**
   * Navigate to admin settings
   */
  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  /**
   * Navigate to stats dashboard
   */
  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  /**
   * Navigate to operations request analytics
   */
  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  /**
   * Logout user
   */
  logout(): void {
    this.authService.logout();
  }

  /**
   * PDF Viewer Methods
   */

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageInput = this.currentPage.toString();
      this.cdr.markForCheck();
    }
  }

  nextPage(): void {
    if (this.totalPages > 0 && this.currentPage < this.totalPages) {
      this.currentPage++;
      this.pageInput = this.currentPage.toString();
      this.cdr.markForCheck();
    }
  }

  goToPage(): void {
    const page = parseInt(this.pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.cdr.markForCheck();
    } else {
      // Reset to current page if invalid input
      this.pageInput = this.currentPage.toString();
    }
  }

  /**
   * Handle page change event from PDF viewer (triggered by scroll)
   * Updates currentPage and pageInput in real-time as user scrolls through PDF
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.pageInput = this.currentPage.toString();
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    
    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.goToPage();
      }
      return;
    }
    
    // Block non-numeric characters
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  zoomIn(): void {
    if (this.zoom < 3.0) {
      this.zoom = Math.min(3.0, this.zoom + 0.25);
      this.zoomPercentage = Math.round(this.zoom * 100);
      this.cdr.markForCheck();
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.zoom = Math.max(0.5, this.zoom - 0.25);
      this.zoomPercentage = Math.round(this.zoom * 100);
      this.cdr.markForCheck();
    }
  }

  setZoomByPercentage(): void {
    const percent = parseInt(String(this.zoomPercentage), 10);
    if (!isNaN(percent)) {
      // Clamp value to valid range [50, 300]
      const clampedPercent = Math.min(300, Math.max(50, percent));
      this.zoom = clampedPercent / 100;
      this.zoomPercentage = clampedPercent;
      this.cdr.markForCheck();
    } else {
      this.zoomPercentage = Math.round(this.zoom * 100);
    }
  }

  onZoomInputKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    
    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.setZoomByPercentage();
      }
      return;
    }
    
    // Block non-numeric characters
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Handle PDF wheel event for scroll-based zoom
   * Ctrl+scroll to zoom while maintaining cursor position
   */
  private onPdfWheel(event: WheelEvent): void {
    // Only zoom when Ctrl key is held (standard zoom behavior)
    if (!event.ctrlKey) return;
    
    // Prevent browser zoom
    event.preventDefault();
    
    if (!this.pdfSrc) return;
    
    const container = this.pdfContainer?.nativeElement;
    if (!container) return;
    
    const viewer = container.querySelector('.ng2-pdf-viewer-container') as HTMLElement;
    if (!viewer) return;
    
    // Get cursor position relative to container
    const rect = container.getBoundingClientRect();
    const cursorX = event.clientX - rect.left;
    const cursorY = event.clientY - rect.top;
    
    // Calculate scroll position ratios (where cursor is relative to scroll area)
    const scrollLeft = viewer.scrollLeft;
    const scrollTop = viewer.scrollTop;
    const scrollWidth = viewer.scrollWidth;
    const scrollHeight = viewer.scrollHeight;
    
    // Calculate cursor position as ratio of total scroll area
    const ratioX = scrollWidth > 0 ? (scrollLeft + cursorX) / scrollWidth : 0;
    const ratioY = scrollHeight > 0 ? (scrollTop + cursorY) / scrollHeight : 0;
    
    // Store old zoom
    const oldZoom = this.zoom;
    
    // Calculate zoom delta - smooth increments (10% per scroll tick)
    const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(3.0, Math.max(0.5, this.zoom + zoomDelta));
    
    if (newZoom === oldZoom) return;
    
    // Update zoom
    this.zoom = newZoom;
    this.zoomPercentage = Math.round(this.zoom * 100);
    this.cdr.detectChanges();
    
    // After Angular updates the view, adjust scroll to keep cursor position
    setTimeout(() => {
      const newScrollWidth = viewer.scrollWidth;
      const newScrollHeight = viewer.scrollHeight;
      
      // Calculate new scroll position to maintain cursor focus point
      const newScrollLeft = (ratioX * newScrollWidth) - cursorX;
      const newScrollTop = (ratioY * newScrollHeight) - cursorY;
      
      viewer.scrollLeft = Math.max(0, newScrollLeft);
      viewer.scrollTop = Math.max(0, newScrollTop);
    }, 10);
  }

  onPdfContainerClick(event: MouseEvent): void {
    // Placeholder for PDF container interactions
  }

  /**
   * Handle PDF document load event
   * Called by ng2-pdf-viewer when PDF is fully loaded and parsed
   * Updates total page count for UI and reapplies stored rotations
   */
  onPdfDocumentLoad(pdf: any): void {
    try {
      // ng2-pdf-viewer fires 'after-load-complete' event with the PDF document object
      const numPages = pdf?.numPages || 0;
      
      if (numPages > 0) {
        this.totalPages = numPages;
        this.pdfReady = true;  // Mark PDF as ready for interaction
        // Reset to first page when PDF first loads
        if (this.currentPage > numPages) {
          this.currentPage = 1;
          this.pageInput = '1';
        }
        
        // Reapply any stored page rotations after PDF loads
        this.applyAllPageRotations();
        this.cdr.detectChanges();
      } else {
      }
    } catch (error) {
    }
  }

  /**
   * Format field value for display
   */
  formatFieldValue(value: any): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  /**
   * Rotate current page by 90 degrees clockwise
   * Uses cumulative rotation to always rotate in same direction
   */
  rotatePdf(): void {
    if (!this.pdfSrc || !this.pdfReady) {
      return;
    }

    // Get current page's cumulative rotation (default to 0 if not set)
    const currentRotation = this.pageRotations.get(this.currentPage) || 0;
    
    // Always increment by 90° - never reset to 0°
    // This ensures CSS always animates clockwise: 0° → 90° → 180° → 270° → 360° → 450° ...
    const newRotation = currentRotation + 90;
    
    // Store cumulative rotation (don't use modulo)
    this.pageRotations.set(this.currentPage, newRotation);
    
    // Apply rotation with smooth animation
    this.applyPageRotation(this.currentPage, newRotation, true);
    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Apply CSS transform rotation to a specific page container
   * @param pageNumber - Page number (1-indexed)
   * @param rotation - Cumulative rotation in degrees (0, 90, 180, 270, 360, 450, ...)
   * @param withTransition - Whether to animate the rotation (default: true)
   */
  private applyPageRotation(pageNumber: number, rotation: number, withTransition: boolean = true): void {
    if (!this.pdfContainer?.nativeElement) {
      return;
    }

    // Find the page container in DOM using data-page-number attribute
    // ng2-pdf-viewer renders pages with data-page-number attribute
    const viewer = this.pdfContainer.nativeElement;
    const pageContainer = viewer.querySelector(`[data-page-number="${pageNumber}"]`) as HTMLElement;
    
    if (!pageContainer) {
      return;
    }

    // Calculate visual rotation (0-360 range) for aspect ratio calculations
    const visualRotation = rotation % 360;
    
    // Add or remove transition
    if (withTransition) {
      pageContainer.style.transition = 'transform 0.3s ease-in-out';
    } else {
      pageContainer.style.transition = 'none';
    }
    
    // Get the canvas element inside the page to determine dimensions
    const canvas = pageContainer.querySelector('canvas') as HTMLCanvasElement;
    
    let transformValue = `rotate(${rotation}deg)`;
    
    // For 90° and 270° visual rotations, apply scaling to fit within bounds
    // because the page dimensions are effectively swapped (width becomes height)
    if (canvas && (visualRotation === 90 || visualRotation === 270)) {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      // Calculate aspect ratio to determine how much to scale
      if (canvasWidth > 0 && canvasHeight > 0) {
        const aspectRatio = canvasWidth / canvasHeight;
        
        // Scale factor to make rotated page fit within original bounds
        const scaleFactor = aspectRatio !== 1 ? Math.min(1 / aspectRatio, aspectRatio) : 1;
        
        transformValue = `rotate(${rotation}deg) scale(${scaleFactor})`;
      }
    }
    
    // Apply transform
    pageContainer.style.transform = transformValue;
    pageContainer.style.transformOrigin = 'center center';
  }

  /**
   * Apply all stored page rotations to visible pages
   * Called after PDF loads or page changes to reapply rotations
   */
  private applyAllPageRotations(): void {
    if (!this.pdfReady || this.pageRotations.size === 0) {
      return;
    }

    // Apply rotation to each page that has a stored rotation
    // Use withTransition=false to apply instantly (no animation when reapplying)
    this.pageRotations.forEach((rotation, pageNumber) => {
      this.applyPageRotation(pageNumber, rotation, false);
    });
  }

  /**
   * Get page binding for PDF viewer, or undefined to suppress page binding during rotation
   * Prevents page jumps during rotation animations
   */
  getPdfPageBinding(): number | undefined {
    return this.pdfReady ? this.currentPage : 1;
  }

  /**
   * Get rotation for current page (defaults to 0 if not set)
   */
  getCurrentPageRotation(): number {
    return this.pageRotations.get(this.currentPage) || 0;
  }

  /**
   * Handle resize divider mouse down event
   * Initiates dragging to resize columns
   */
  startResize(event: MouseEvent): void {
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartSplit = this.splitPosition;
    
    // Add class to document to show col-resize cursor globally
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    // Add mouse move and mouse up listeners
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    
    // Prevent text selection during drag
    event.preventDefault();
  }

  /**
   * Handle mouse move event for resizing
   */
  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) {
      return;
    }

    // Get the content grid container
    const contentGrid = document.querySelector('.content-grid') as HTMLElement;
    if (!contentGrid) {
      return;
    }

    // Calculate the width of the container
    const containerWidth = contentGrid.offsetWidth;
    
    // Calculate the change in mouse position
    const deltaX = event.clientX - this.dragStartX;
    
    // Calculate the new split position as a percentage
    // Subtract 20px (divider width) and divide gaps proportionally
    const containerWidthAdjusted = containerWidth - 20; // 20px divider
    const percentageChange = (deltaX / containerWidthAdjusted) * 100;
    
    let newSplitPosition = this.dragStartSplit + percentageChange;
    
    // Constrain between 20% and 80% to prevent one panel from becoming too small
    newSplitPosition = Math.max(20, Math.min(80, newSplitPosition));
    
    this.splitPosition = newSplitPosition;
    this.cdr.markForCheck();
  }

  /**
   * Handle mouse up event to stop resizing
   */
  private onMouseUp(): void {
    this.isDragging = false;
    
    // Reset cursor and selection
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
    
    // Remove event listeners
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onMouseUp.bind(this));
  }

  /**
   * Filter extracted fields based on search term
   * Matches field name case-insensitively
   */
  getFilteredFields(): ExtractedField[] {
    if (!this.searchTerm.trim()) {
      return this.extractedFields;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();
    return this.extractedFields.filter(field =>
      field.fieldName.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Count of schema fields that have been extracted (additional/leftover fields excluded)
   */
  getExtractedFieldsCount(): number {
    return this.extractedFieldsCount;
  }

  /**
   * Total number of fields in the field schema (additional/leftover fields excluded)
   */
  getTotalFieldsCount(): number {
    return this.fieldSchemaFields.length;
  }

  /**
   * Get display label for list field badge
   * Uses field_datatype from schema to determine if field is an array
   * For array fields with special features (signature/date/checkbox/seal), show "Feature (count)"
   * For array fields without special features, show "Group (count)"
   * For non-array fields with special features, show "Feature"
   * For non-array fields without special features, show nothing
   */
  getListFieldBadgeLabel(field: ExtractedListField): string {
    const specialFeatures = ['signature', 'date', 'checkbox', 'seal'];
    const feature = field.feature?.toLowerCase() || '';
    const isArrayField = field.fieldDatatype?.toLowerCase() === 'array';
    const valueCount = field.values.length;
    
    // For array fields (based on schema field_datatype)
    if (isArrayField) {
      // If it's a special feature, show feature name with count
      if (specialFeatures.includes(feature)) {
        const featureName = feature.charAt(0).toUpperCase() + feature.slice(1);
        return `${featureName} (${valueCount})`;
      }
      
      // For other array fields, show "Group (count)"
      return `Group (${valueCount})`;
    }
    
    // For non-array fields, show feature name only if it's a special feature
    if (specialFeatures.includes(feature)) {
      const featureName = feature.charAt(0).toUpperCase() + feature.slice(1);
      return featureName;
    }
    
    // No label for non-array fields without special features
    return '';
  }
}
